import React, { useState } from 'react';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useCampaignTemplates, useDeleteTemplate } from '@/hooks/useCampaignTemplates';
import { CampaignTemplate } from '@/services/campaignTemplateService';
import CampaignTemplateEditor from './CampaignTemplateEditor';
import { format } from 'date-fns';

interface Props {
  companyId: string;
}

const CampaignTemplatesList: React.FC<Props> = ({ companyId }) => {
  const { data: templates = [], isLoading } = useCampaignTemplates(companyId);
  const deleteMutation = useDeleteTemplate();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<CampaignTemplate | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditTemplate(null); setEditorOpen(true); }}><Plus className="h-4 w-4 mr-1.5" />Create Template</Button>
      </div>

      {isLoading ? (
        <p className="text-center text-muted-foreground py-8">Loading templates...</p>
      ) : templates.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No templates yet.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {templates.map(t => (
            <Card key={t.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base">{t.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditTemplate(t); setEditorOpen(true); }}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { const clone = { ...t, id: undefined as any, name: `Copy of ${t.name}` }; setEditTemplate(clone); setEditorOpen(true); }}><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteId(t.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex gap-1 flex-wrap">
                  <Badge variant="outline">{t.template_type}</Badge>
                  {t.category && <Badge variant="secondary">{t.category}</Badge>}
                  <Badge variant={t.is_active ? 'default' : 'secondary'}>{t.is_active ? 'Active' : 'Inactive'}</Badge>
                </div>
                <p className="text-sm text-muted-foreground truncate">{t.subject_template}</p>
                <p className="text-xs text-muted-foreground">Updated {format(new Date(t.updated_at), 'MMM d, yyyy')}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CampaignTemplateEditor open={editorOpen} onOpenChange={setEditorOpen} companyId={companyId} template={editTemplate} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete template?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignTemplatesList;
